import {
  Box,
  Button,
  Card,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import HomeIcon from '@mui/icons-material/Home';
import ImageIcon from '@mui/icons-material/Image';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import Folder from './Folder';
import { useEffect, useState } from 'react';
import { getContent } from 'src/redux/actions/Content';
import { api, resolveSrc } from '../../api/endpoints';
import { changeFolder } from 'src/redux/actions/FileFolder';
import { getNiceTimestamp } from 'src/utils/get-nice-timestamp';
import { splitYoutubePending } from 'src/utils/youtubeProcessing';
import YoutubeProcessingDialog from './YoutubeProcessingDialog';
import { isLiveWidget } from 'src/utils/liveWidgets';

const PMW_PROCESSING_MS = 2 * 60 * 1000;

const SelectFilesModal = ({ open, setOpen, onSelectFiles, filesToExclude }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { userFolders, contentFolders } = useSelector(
    (state) => ({
      userFolders: state.FileFolder.userFolders.filter(
        (f) =>
          f.parent === state.FileFolder.currentFolder &&
          f?.folderFor === 'content',
      ),
      contentFolders: state.FileFolder.userFolders.filter(
        (f) => f?.folderFor === 'content',
      ),
    }),
    shallowEqual,
  );
  const { content, contentsCount } = useSelector((state) => state.Content);

  const [sortOption, setSortOption] = useState('asc');
  const [selectFilesQuery, setSelectFilesQuery] = useState({
    name: '',
    type: '',
    page: 0,
    sortType: 'createdAt',
    sortDirection: -1,
    folder: '',
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pmwUnavailableNames, setPmwUnavailableNames] = useState([]);
  const [isPmwUnavailableModalOpen, setIsPmwUnavailableModalOpen] =
    useState(false);
  const [ytPendingNames, setYtPendingNames] = useState([]);

  const [isAddButtonBottomShown, setIsAddButtonBottomShown] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  useEffect(() => {
    dispatch(
      getContent(
        selectFilesQuery.page,
        selectFilesQuery.name,
        selectFilesQuery.sortType,
        selectFilesQuery.sortDirection,
        selectFilesQuery.type,
        selectFilesQuery.folder == 'root' ? '' : selectFilesQuery.folder,
      ),
    );
  }, [selectFilesQuery]);

  useEffect(() => {
    switch (sortOption) {
      case 'asc':
        setSelectFilesQuery({
          ...selectFilesQuery,
          sortType: 'createdAt',
          sortDirection: -1,
        });
        break;
      case 'desc':
        setSelectFilesQuery({
          ...selectFilesQuery,
          sortType: 'createdAt',
          sortDirection: 1,
        });
        break;
      case 'name_desc':
        setSelectFilesQuery({
          ...selectFilesQuery,
          sortType: 'name',
          sortDirection: 1,
        });
        break;
      case 'name_asc':
        setSelectFilesQuery({
          ...selectFilesQuery,
          sortType: 'name',
          sortDirection: -1,
        });
        break;
      default:
        setSelectFilesQuery({
          ...selectFilesQuery,
          sortType: 'createdAt',
          sortDirection: 1,
        });
        break;
    }
  }, [sortOption]);

  useEffect(() => {
    const pendingTimes = content
      .filter(
        (file) =>
          file?.type === 'widget' && file?.widget?.widgetCat === 'postermywall',
      )
      .map(
        (file) =>
          new Date(file.updatedAt || file.createdAt).getTime() +
          PMW_PROCESSING_MS -
          Date.now(),
      )
      .filter((remainingMs) => Number.isFinite(remainingMs) && remainingMs > 0);

    if (!pendingTimes.length) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => {
        setCurrentTime(Date.now());
      },
      Math.min(...pendingTimes),
    );

    return () => window.clearTimeout(timeoutId);
  }, [content, currentTime]);

  const handleClose = () => {
    setSelectedFiles([]);
    setOpen(false);
  };

  const handleSave = async () => {
    const isPostermywallWidget = (file) =>
      file?.type === 'widget' && file?.widget?.widgetCat === 'postermywall';

    const { allowed, pending } = splitYoutubePending(selectedFiles);
    if (pending.length > 0) {
      setYtPendingNames(pending.map((file) => file.name));
      setSelectedFiles(pending);
      if (allowed.length === 0) {
        return;
      }
    }

    const checks = await Promise.all(
      allowed.map(async (file) => {
        if (!isPostermywallWidget(file) || !file?.widget?.url) {
          return { id: file.id, name: file.name, isAvailable: true };
        }

        try {
          const result = await api.post('/content/check-media-available', {
            url: file.widget.url,
          });

          return {
            id: file.id,
            name: file.name,
            isAvailable: !!result?.data?.isAvailable,
          };
        } catch (error) {
          return {
            id: file.id,
            name: file.name,
            isAvailable: false,
          };
        }
      }),
    );

    const availableIds = checks
      .filter((item) => item.isAvailable)
      .map((item) => item.id);
    const unavailableNames = checks
      .filter((item) => !item.isAvailable)
      .map((item) => item.name);

    if (availableIds.length > 0) {
      onSelectFiles(
        availableIds,
        allowed.filter((file) => availableIds.includes(file.id)),
      );
    }

    if (unavailableNames.length > 0) {
      setPmwUnavailableNames(unavailableNames);
      setIsPmwUnavailableModalOpen(true);
    }

    if (
      pending.length > 0 ||
      (availableIds.length === 0 && unavailableNames.length > 0)
    ) {
      return;
    }

    setSelectFilesQuery({
      name: '',
      type: '',
      page: 0,
      sortType: '',
      sortDirection: '',
      folder: '',
    });
    setSelectedFiles([]);
    setOpen(false);
  };

  const handleTableScroll = (e) => {
    const scrollTop = e.currentTarget.scrollTop;
    setIsAddButtonBottomShown(scrollTop > 100);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth={'xl'}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            p: 3,
            position: 'relative',
            // overflow: "hidden"
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '30px',
            }}
          >
            <Button variant='contained' onClick={handleSave}>
              {t('views.selectFilesModal.add')}
            </Button>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          {!isAddButtonBottomShown && (
            <>
              {selectFilesQuery.folder ? (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '5px',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    onClick={() => {
                      setSelectFilesQuery({
                        ...selectFilesQuery,
                        folder: '',
                        page: 0,
                      });
                      dispatch(changeFolder('root'));
                    }}
                  >
                    <HomeIcon color='primary' />
                  </IconButton>
                  <Typography variant='button'>
                    {
                      contentFolders.find(
                        (f) => f._id == selectFilesQuery.folder,
                      )?.name
                    }
                  </Typography>
                </Box>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  gap: '20px',
                }}
              >
                {userFolders.map((f) => {
                  return (
                    <Folder
                      folder={f}
                      setSelectFilesQuery={setSelectFilesQuery}
                      selectFilesQuery={selectFilesQuery}
                    />
                  );
                })}
              </Box>
              <Card
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '10px',
                  p: 2,
                  backgroundColor: '#131b2a',
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    width: '70%',
                  }}
                  component='form'
                  onSubmit={(e) => {
                    e.preventDefault();
                    setSelectFilesQuery({
                      ...selectFilesQuery,
                      name: e.target[0].value,
                      page: 0,
                    });
                  }}
                  onReset={() =>
                    setSelectFilesQuery({ ...selectFilesQuery, name: '' })
                  }
                >
                  <OutlinedInput
                    sx={{
                      minWidth: '200px',
                      width: '100%',
                      maxWidth: '400px',
                    }}
                    startAdornment={
                      <InputAdornment position='start'>
                        <SearchIcon color='disabled' />
                      </InputAdornment>
                    }
                    placeholder={t('components.redesignBodyHeader.search')}
                    id='searchByNameInput'
                  />
                  <IconButton type='submit'>
                    <SearchIcon color='primary' />
                  </IconButton>
                  {selectFilesQuery.name ? (
                    <IconButton type='reset'>
                      <CloseIcon color='error' />
                    </IconButton>
                  ) : null}
                </Box>
                <FormControl
                  sx={{
                    width: '200px',
                  }}
                >
                  <InputLabel id='sortOptions'>
                    {t('components.redesignBodyHeader.sortBy')}
                  </InputLabel>
                  <Select
                    labelId='sortOptions'
                    id='sortOptionsSelect'
                    value={sortOption}
                    label={t('components.redesignBodyHeader.sortBy')}
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <MenuItem value={'desc'}>
                      ↓ {t('components.redesignBodyHeader.createDate')}
                    </MenuItem>
                    <MenuItem value={'asc'}>
                      ↑ {t('components.redesignBodyHeader.createDate')}
                    </MenuItem>
                    <MenuItem value={'name_desc'}>
                      ↓ {t('components.redesignBodyHeader.name')}
                    </MenuItem>
                    <MenuItem value={'name_asc'}>
                      ↑ {t('components.redesignBodyHeader.name')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Card>
            </>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
            }}
          >
            <Button
              onClick={() =>
                setSelectFilesQuery({ ...selectFilesQuery, type: '' })
              }
              startIcon={<FileCopyIcon />}
            >
              {t('views.redesignFile.all')}
            </Button>
            <Button
              onClick={() =>
                setSelectFilesQuery({ ...selectFilesQuery, type: 'images' })
              }
              startIcon={<ImageIcon />}
            >
              {t('views.redesignFile.images')}
            </Button>
            <Button
              onClick={() =>
                setSelectFilesQuery({ ...selectFilesQuery, type: 'videos' })
              }
              startIcon={<SlowMotionVideoIcon />}
            >
              {t('views.redesignFile.videos')}
            </Button>
            <Button
              onClick={() =>
                setSelectFilesQuery({ ...selectFilesQuery, type: 'apps' })
              }
              startIcon={<AppRegistrationIcon />}
            >
              {t('views.redesignFile.apps')}
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            {selectedFiles.map((sf) => {
              // if(content.find(f => f.id == sf.id)) {
              //     return (
              //         <Button size="small" startIcon={<HighlightOffOutlinedIcon/>} onClick={() => setSelectedFiles((prevSelectedFiles) => prevSelectedFiles.filter((item) => item !== sf))}>
              //             {content.find(f => f.id == sf)?.name.length > 15 ? `${content.find(f => f.id == sf)?.name.slice(0, 15)}...` : content.find(f => f.id == sf)?.name}
              //         </Button>
              //     )
              // }
              return (
                <Button
                  size='small'
                  color='secondary'
                  startIcon={<HighlightOffOutlinedIcon />}
                  onClick={() =>
                    setSelectedFiles((prevSelectedFiles) =>
                      prevSelectedFiles.filter((item) => item.id !== sf.id),
                    )
                  }
                >
                  {sf.name.length > 15 ? `${sf.name.slice(0, 15)}...` : sf.name}
                </Button>
              );
            })}
          </Box>
          <TableContainer
            sx={{
              overflowY: 'scroll',
              height: `calc(100vh - ${isAddButtonBottomShown ? '350px' : '600px'})`,
            }}
            onScroll={handleTableScroll}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell align='left'>
                    {t('views.selectFilesModal.preview')}
                  </TableCell>
                  <TableCell align='left'>
                    {t('views.selectFilesModal.type')}
                  </TableCell>
                  <TableCell>{t('views.selectFilesModal.fileName')}</TableCell>
                  <TableCell align='right'>
                    {t('views.selectFilesModal.createdAtCol')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {content.filter((file) => !isLiveWidget(file)).map((file) =>
                  (() => {
                    const isPostermywallWidget =
                      file?.type === 'widget' &&
                      file?.widget?.widgetCat === 'postermywall';
                    const createdAtMs = new Date(
                      file?.updatedAt || file?.createdAt,
                    ).getTime();
                    const remainingPmwProcessingMs =
                      isPostermywallWidget && Number.isFinite(createdAtMs)
                        ? createdAtMs + PMW_PROCESSING_MS - currentTime
                        : 0;
                    const showPostermywallSpinner =
                      remainingPmwProcessingMs > 0;

                    return (
                      <TableRow hover key={file.id}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            disabled={
                              filesToExclude?.includes(file.id) ||
                              (isPostermywallWidget && showPostermywallSpinner)
                            }
                            checked={
                              selectedFiles.find((sf) => sf.id == file.id) ||
                              filesToExclude?.includes(file.id)
                                ? true
                                : false
                            }
                            onChange={(event) => {
                              if (event.target.checked) {
                                setSelectedFiles([
                                  ...selectedFiles,
                                  {
                                    id: file.id,
                                    name: file.name,
                                    type: file.type,
                                    widget: file.widget,
                                    ytStatus: file.ytStatus,
                                    url: file.url,
                                    embed: file.embed,
                                    duration: file.duration,
                                    size: file.size,
                                  },
                                ]);
                              } else {
                                setSelectedFiles((prevSelectedFiles) =>
                                  prevSelectedFiles.filter(
                                    (item) => item.id !== file.id,
                                  ),
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ maxWidth: '5rem', minWidth: '150px' }}
                        >
                          {file.type === 'image' && (
                            <img
                              src={resolveSrc(file?.url)}
                              style={{
                                maxWidth: '110px',
                                height: 'auto',
                                maxHeight: '60px',
                                display: 'block',
                                margin: 'auto',
                              }}
                            />
                          )}
                          {(file.type === 'widget' ||
                            file.type === 'split_screen') && (
                            <Box
                              sx={{
                                position: 'relative',
                                width: '110px',
                                height: '60px',
                                margin: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src={`${file.widget?.widgetIcon}`}
                                style={{
                                  maxWidth: '110px',
                                  height: 'auto',
                                  maxHeight: '60px',
                                  display: 'block',
                                  margin: 'auto',
                                  opacity:
                                    isPostermywallWidget &&
                                    showPostermywallSpinner
                                      ? 0.45
                                      : 1,
                                }}
                              />
                              {isPostermywallWidget &&
                              showPostermywallSpinner ? (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                  }}
                                >
                                  <CircularProgress
                                    size={20}
                                    thickness={6}
                                    sx={{ color: '#fff' }}
                                  />
                                </Box>
                              ) : null}
                            </Box>
                          )}

                          {file.type === 'video' && (
                            <video
                              src={resolveSrc(file?.url)}
                              controls={false}
                              autoPlay={false}
                              muted
                              style={{
                                width: '110px',
                                height: 'auto',
                                maxHeight: '60px',
                                margin: 'auto',
                              }}
                            />
                          )}
                          {file.embed === 'embed' && (
                            <embed
                              src={`${file.url}`}
                              style={{
                                maxWidth: '110px',
                                height: 'auto',
                                maxHeight: '60px',
                                margin: 'auto',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align='left'>
                          {file.type === 'image' && (
                            <ImageIcon htmlColor='#6c737f' />
                          )}
                          {file.type === 'widget' && (
                            <AppRegistrationIcon htmlColor='#6c737f' />
                          )}
                          {file.type === 'video' && (
                            <SlowMotionVideoIcon htmlColor='#6c737f' />
                          )}
                          {file.embed === 'embed' && (
                            <AppRegistrationIcon htmlColor='#6c737f' />
                          )}
                          {!(
                            file.type === 'image' ||
                            file.type === 'widget' ||
                            file.type === 'video' ||
                            file.embed === 'embed'
                          ) && <AppRegistrationIcon htmlColor='#6c737f' />}
                        </TableCell>
                        <TableCell sx={{ width: '100%' }}>
                          <Stack alignItems='left' direction='row' spacing={1}>
                            {file.name.length > 30 ? (
                              <p title={file.name}>
                                {file.name.slice(0, 30)}...
                              </p>
                            ) : (
                              <Typography variant='body2'>
                                {file.name}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography
                            color='text.secondary'
                            variant='caption'
                            sx={{ display: { md: 'block', xs: 'none' } }}
                            width={180}
                            textAlign='end'
                          >
                            {getNiceTimestamp(new Date(file?.createdAt), true)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })(),
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <IconButton
              onClick={() =>
                selectFilesQuery.page > 0 &&
                setSelectFilesQuery({
                  ...selectFilesQuery,
                  page: selectFilesQuery.page - 1,
                })
              }
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <Typography>
              {selectFilesQuery.page + 1}/{Math.floor(contentsCount / 30) + 1}
            </Typography>
            <IconButton
              onClick={() =>
                selectFilesQuery.page < Math.floor(contentsCount / 30) &&
                setSelectFilesQuery({
                  ...selectFilesQuery,
                  page: selectFilesQuery.page + 1,
                })
              }
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
      <Dialog
        open={isPmwUnavailableModalOpen}
        onClose={() => setIsPmwUnavailableModalOpen(false)}
        fullWidth
        maxWidth={'sm'}
      >
        <DialogTitle>
          {t('views.selectFilesModal.fileUnavailableTitle', {
            defaultValue: 'File unavailable',
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {t('views.selectFilesModal.fileUnavailableMessage', {
              defaultValue:
                'Some PosterMyWall designs are not available yet. Please try again later.',
            })}
          </Typography>
          {pmwUnavailableNames.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {pmwUnavailableNames.map((fileName, index) => (
                <Typography
                  key={`${fileName}-${index}`}
                  variant='caption'
                  color='text.secondary'
                >
                  {fileName}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsPmwUnavailableModalOpen(false)}
            variant='contained'
          >
            {t('views.selectFilesModal.ok', { defaultValue: 'OK' })}
          </Button>
        </DialogActions>
      </Dialog>
      <YoutubeProcessingDialog
        open={ytPendingNames.length > 0}
        onClose={() => setYtPendingNames([])}
        names={ytPendingNames}
      />
    </>
  );
};

export default SelectFilesModal;
